import { getUserLabels, userFields } from '@openx/components-administration/lib/constants.ts';
import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext.tsx';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { PageNotFound, type PageNotFoundProps } from '@openx/components-core/lib/PageNotFound/PageNotFound.tsx';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions.tsx';
import { PageHeader } from '@openx/components-header/PageHeader.tsx';
import { useTranslation } from 'react-i18next';

type UserDetailsProps = {
  handleGoBack: () => void;
  userId: string;
  titlePrefix: PageNotFoundProps['appName'];
};

export const UserDetails = ({ handleGoBack, userId, titlePrefix }: UserDetailsProps) => {
  const { t } = useTranslation();
  const { useUserFetch, useUserPageHeaderActions } = useAdministrationContext();
  const { user, loading } = useUserFetch(userId);
  const actions = useUserPageHeaderActions({ id: userId, name: `${user?.first_name} ${user?.last_name}` });
  const userLabels = getUserLabels(t);

  if (loading) return <Loader />;

  if (!user) {
    return <PageNotFound appName={'OpenXSelect'} />;
  }

  const { first_name, last_name, account, role } = user;

  return (
    <PageHeader
      goBack={handleGoBack}
      render={() => <HeaderActions actions={actions} />}
      title={t('Details: {firstName} {lastName}', { firstName: first_name, lastName: last_name })}
      titlePrefix={titlePrefix}
    >
      <FormSection divider title={t('User Details')}>
        <FieldDetails title={userLabels[userFields.FIRST_NAME]} value={first_name} />
        <FieldDetails title={userLabels[userFields.LAST_NAME]} value={last_name} />
        <FieldDetails title={userLabels[userFields.ACCOUNT]} value={account?.name} />
        <FieldDetails title={userLabels[userFields.ROLE]} value={role} />
      </FormSection>
    </PageHeader>
  );
};
